// Which build this bundle is, and whether its origin now serves a newer one
// (INT-7, plan section 10).
//
// A kiosk tab stays open for days, and Pages publishes a new bundle with
// every release. index.html is never cached, so a reload is all it takes to
// move a screen on; but a reload at the wrong moment loses what the screen
// holds. So a screen learns first and moves later.
//
//   learn  Fetch /build.txt with no cache and compare it with this bundle's
//          own commit. The till asks when the menu says the API runs another
//          build. The dashboard and the admin ask every five minutes, and
//          when their tab comes back into view.
//   move   At a safe moment. The till moves when the cart is empty, no
//          sale waits for an answer, no receipt prints and no address
//          removal is unsent (src/till/rest.ts). The dashboard and the
//          admin move at their next navigation. After a price change that
//          the open bundle cannot compute (TOTAL_MISMATCH), the till moves at
//          once: the draft survives the reload.
//
// A screen moves at most once for each marker value, and the tab remembers
// it in sessionStorage. A Pages rollback, or an edge that serves the old page
// beside the new marker, therefore cannot reload a screen in a loop.
//
// Inside the Bosla app (#392) the till reads no /build.txt: its origin is the
// app's own, and a newer screen is a signed bundle the order store kept,
// which the shell switches to when the till is at rest (src/till/rest.ts).
// There the watch is off, and nothing moves the till at once.

import { BUNDLE_COMMIT, isCommit } from './commit';
import { inShell } from './till/shell';

/** The commit this bundle was built from (commit.ts). */
export { BUNDLE_COMMIT };

/** The till asks at most this often while the API reports another build. */
export const CHECK_GAP_MS = 30_000;

/** The dashboard and the admin ask this often. */
export const SCREEN_CHECK_MS = 5 * 60_000;

// Only a full commit hash (isCommit) is a marker. An old deployment that has
// no build.txt answers with its page instead, and that page is no reason to
// reload.
const MOVED = 'build-reloaded:';

export interface BuildDeps {
	/** This bundle's commit. When it is not a commit, the watch does nothing. */
	own: string;
	now: () => number;
	/** What /build.txt says now. It throws or gives null when it cannot be read. */
	served: () => Promise<string | null>;
	/** The tab's own storage. Null when the browser refuses it. */
	storage: Pick<Storage, 'getItem' | 'setItem'> | null;
	/** Load the page again, here or at a path inside the app. */
	load: (to?: string) => void;
}

export interface BuildWatch {
	/** The newer marker this screen waits to move to, or null. */
	newer(): string | null;
	/** Ask the origin which build it serves. Throttled unless forced. */
	check(force?: boolean): Promise<void>;
	/** The till is at rest: move now if a newer build waits. */
	reloadAtRest(): boolean;
	/** Ask at once, and move at once if the origin serves another build. */
	reloadNow(): Promise<boolean>;
	/** A navigation to `to`: load it as a new page if a newer build waits. */
	loadOnNavigate(to: string): boolean;
	/** Keep the screen from moving while `busy` says so. Returns the release. */
	holdWhile(busy: () => boolean): () => void;
}

export function createBuildWatch(deps: BuildDeps): BuildWatch {
	const enabled = isCommit(deps.own);
	let newer = $state<string | null>(null);
	let lastCheck = Number.NEGATIVE_INFINITY;
	let running: Promise<void> | null = null;
	// Read only when the screen tries to move, never by a template.
	const holds: (() => boolean)[] = [];

	// Whether this tab already moved for the marker. Without storage the
	// answer is yes: a screen that cannot remember must not reload at all.
	function movedFor(marker: string): boolean {
		try {
			return !deps.storage || deps.storage.getItem(MOVED + marker) !== null;
		} catch {
			return true;
		}
	}

	// Written before the load, so the page that comes back finds it.
	function claim(marker: string): boolean {
		if (movedFor(marker) || !deps.storage) return false;
		try {
			deps.storage.setItem(MOVED + marker, '1');
			return deps.storage.getItem(MOVED + marker) === '1';
		} catch {
			return false;
		}
	}

	async function ask(): Promise<void> {
		let marker: string | null;
		try {
			marker = (await deps.served())?.trim() ?? null;
		} catch {
			marker = null;
		}
		if (marker === null || !isCommit(marker)) return;
		if (marker === deps.own) {
			// Pages serves this bundle again, after a rollback of the build
			// this screen was waiting for.
			newer = null;
			return;
		}
		if (!movedFor(marker)) newer = marker;
	}

	function check(force = false): Promise<void> {
		if (!enabled) return Promise.resolve();
		if (running) return running;
		const at = deps.now();
		if (!force && at >= lastCheck && at - lastCheck < CHECK_GAP_MS) return Promise.resolve();
		lastCheck = at;
		running = ask().finally(() => {
			running = null;
		});
		return running;
	}

	function held(): boolean {
		return holds.some((busy) => busy());
	}

	function move(to?: string): boolean {
		const marker = newer;
		if (marker === null || !claim(marker)) return false;
		newer = null;
		deps.load(to);
		return true;
	}

	return {
		newer: () => newer,
		check,
		reloadAtRest: () => !held() && move(),
		async reloadNow() {
			await check(true);
			return move();
		},
		loadOnNavigate: (to) => !held() && move(to),
		holdWhile(busy) {
			holds.push(busy);
			return () => {
				const at = holds.indexOf(busy);
				if (at >= 0) holds.splice(at, 1);
			};
		},
	};
}

const inBrowser = typeof window !== 'undefined';

function tabStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}

// Inside the Bosla app the till's screen comes from the shell, not from an
// origin that publishes a marker.
const inApp = inBrowser && inShell();

// Off in the dev server, which serves no marker, outside a browser, and
// inside the app.
const watch = createBuildWatch({
	own: inBrowser && !import.meta.env.DEV && !inApp ? BUNDLE_COMMIT : '',
	now: () => Date.now(),
	served: async () => {
		const response = await fetch('/build.txt', { cache: 'no-store' });
		return response.ok ? await response.text() : null;
	},
	storage: inBrowser ? tabStorage() : null,
	load: (to) => {
		if (to === undefined) window.location.reload();
		else window.location.assign(to);
	},
});

/** Ask which build the origin serves, at most once every CHECK_GAP_MS. */
export function checkBuild(): Promise<void> {
	return watch.check();
}

/**
 * Ask now, and move now if the origin serves another build; the draft
 * survives the reload. Inside the app it does nothing: the till switches to a
 * newer bundle only at rest (src/till/rest.ts), never over an open cart.
 */
export function reloadNowIfNewer(): Promise<boolean> {
	if (inApp) return Promise.resolve(false);
	return watch.reloadNow();
}

/** For the router: load `to` as a new page when a newer build waits. */
export function loadOnNavigate(to: string): boolean {
	return watch.loadOnNavigate(to);
}

/** Keep the screen from moving while `busy` says so. */
export function holdReloadWhile(busy: () => boolean): () => void {
	return watch.holdWhile(busy);
}

/**
 * Ask every five minutes, and whenever the tab comes back into view. For the
 * dashboard and the admin, which have no menu poll to tell them. Returns the
 * stop.
 */
export function watchBuild(): () => void {
	if (!inBrowser) return () => {};
	const timer = setInterval(() => void watch.check(), SCREEN_CHECK_MS);
	const onShow = () => {
		if (document.visibilityState === 'visible') void watch.check();
	};
	document.addEventListener('visibilitychange', onShow);
	return () => {
		clearInterval(timer);
		document.removeEventListener('visibilitychange', onShow);
	};
}
